import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from './Dashboard';
import { API, useAuth } from '../App';
import { toast } from 'sonner';
import { Plus, Trash2, Globe, Settings2 } from 'lucide-react';

const Activities = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canEdit = isAdmin || user?.role === 'reservation_manager' || user?.role === 'worker';
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPublic, setEditingPublic] = useState(null);
  const [newActivity, setNewActivity] = useState({
    name: '',
    price: 0,
    currency: 'EUR',
    type: 'activity'
  });

  useEffect(() => {
    loadActivities();
  }, []);

  const loadActivities = async () => {
    try {
      const res = await axios.get(`${API}/activities`);
      setActivities(res.data);
    } catch (error) {
      toast.error('Failed to load activities');
    } finally {
      setLoading(false);
    }
  };

  const createActivity = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/activities`, newActivity);
      toast.success('Activity created!');
      setShowModal(false);
      setNewActivity({ name: '', price: 0, currency: 'EUR', type: 'activity' });
      loadActivities();
    } catch (error) {
      toast.error('Failed to create activity');
    }
  };

  const deleteActivity = async (id) => {
    if (!window.confirm('Delete this activity?')) return;
    try {
      await axios.delete(`${API}/activities/${id}`);
      toast.success('Activity deleted');
      loadActivities();
    } catch (error) {
      toast.error('Failed to delete activity');
    }
  };

  const updatePublicSettings = async (id, settings) => {
    try {
      await axios.put(`${API}/activities/${id}/public-settings`, settings);
      toast.success('Settings updated');
      loadActivities();
      setEditingPublic(null);
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const getBadgeColor = (type) => {
    switch (type) {
      case 'service': return 'bg-blue-100 text-blue-800';
      case 'activity': return 'bg-green-100 text-green-800';
      case 'trip': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-3xl font-bold text-gray-900" data-testid="activities-title">Activities & Services</h2>
          {canEdit && (
            <button
              onClick={() => setShowModal(true)}
              className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
              data-testid="new-activity-btn"
            >
              <Plus className="w-4 h-4" />
              <span>New Activity</span>
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {activities.map((activity) => (
            <div key={activity.id} className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <div className="flex justify-between items-start mb-3">
                <h3 className="text-lg font-semibold text-gray-900">{activity.name}</h3>
                <div className="flex items-center gap-1">
                  {activity.show_public && (
                    <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs" title="Visible to affiliates">
                      <Globe className="w-3 h-3 inline" /> Public
                    </span>
                  )}
                  {isAdmin && (
                    <button onClick={() => setEditingPublic(editingPublic === activity.id ? null : activity.id)}
                      className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100" title="Affiliate settings">
                      <Settings2 className="w-4 h-4" />
                    </button>
                  )}
                  {canEdit && (
                    <button onClick={() => deleteActivity(activity.id)} className="text-red-600 hover:text-red-700"
                      data-testid={`delete-activity-${activity.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-blue-600">{activity.price} {activity.currency}</span>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getBadgeColor(activity.type)}`}>{activity.type}</span>
              </div>

              {/* Affiliate Settings Panel (Admin only) */}
              {isAdmin && editingPublic === activity.id && (
                <AffiliateSettingsPanel activity={activity} onSave={(s) => updatePublicSettings(activity.id, s)} onCancel={() => setEditingPublic(null)} />
              )}
            </div>
          ))}
        </div>

        {/* Create Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full">
              <h3 className="text-2xl font-bold mb-4">New Activity/Service</h3>
              <form onSubmit={createActivity} className="space-y-4">
                <input type="text" placeholder="Activity Name" required value={newActivity.name}
                  onChange={(e) => setNewActivity({ ...newActivity, name: e.target.value })} className="w-full px-3 py-2 border rounded" />
                <div className="flex space-x-2">
                  <input type="number" placeholder="Price" required value={newActivity.price}
                    onChange={(e) => setNewActivity({ ...newActivity, price: parseFloat(e.target.value) })} className="flex-1 px-3 py-2 border rounded" />
                  <select value={newActivity.currency} onChange={(e) => setNewActivity({ ...newActivity, currency: e.target.value })} className="px-3 py-2 border rounded">
                    <option value="EUR">EUR</option><option value="USD">USD</option><option value="DT">DT</option>
                  </select>
                </div>
                <select value={newActivity.type} onChange={(e) => setNewActivity({ ...newActivity, type: e.target.value })} className="w-full px-3 py-2 border rounded">
                  <option value="service">Service</option><option value="activity">Activity</option><option value="trip">Trip</option>
                </select>
                <div className="flex space-x-3">
                  <button type="submit" className="flex-1 bg-blue-600 text-white py-2 rounded hover:bg-blue-700">Create</button>
                  <button type="button" onClick={() => setShowModal(false)} className="flex-1 bg-gray-600 text-white py-2 rounded hover:bg-gray-700">Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

const AffiliateSettingsPanel = ({ activity, onSave, onCancel }) => {
  const [settings, setSettings] = useState({
    show_public: activity.show_public || false,
    b2b_price: activity.b2b_price || 0,
    b2b_currency: activity.b2b_currency || 'EUR',
    b2c_price: activity.b2c_price || 0,
    b2c_currency: activity.b2c_currency || 'EUR',
    description: activity.description || ''
  });

  return (
    <div className="mt-4 pt-4 border-t border-gray-200 space-y-3" data-testid={`affiliate-settings-${activity.id}`}>
      <h4 className="text-sm font-semibold text-emerald-700 flex items-center gap-1">
        <Globe className="w-4 h-4" /> Affiliate Settings
      </h4>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={settings.show_public}
          onChange={e => setSettings(s => ({...s, show_public: e.target.checked}))}
          className="rounded border-gray-300 text-emerald-600" />
        Show to affiliates (Public)
      </label>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs text-gray-500">B2B Price</label>
          <div className="flex gap-1">
            <input type="number" step="0.01" value={settings.b2b_price}
              onChange={e => setSettings(s => ({...s, b2b_price: parseFloat(e.target.value) || 0}))}
              className="w-full px-2 py-1 border rounded text-sm" />
            <select value={settings.b2b_currency} onChange={e => setSettings(s => ({...s, b2b_currency: e.target.value}))}
              className="px-1 py-1 border rounded text-xs">
              <option>EUR</option><option>USD</option><option>DT</option>
            </select>
          </div>
        </div>
        <div>
          <label className="text-xs text-gray-500">B2C Price (recommended)</label>
          <div className="flex gap-1">
            <input type="number" step="0.01" value={settings.b2c_price}
              onChange={e => setSettings(s => ({...s, b2c_price: parseFloat(e.target.value) || 0}))}
              className="w-full px-2 py-1 border rounded text-sm" />
            <select value={settings.b2c_currency} onChange={e => setSettings(s => ({...s, b2c_currency: e.target.value}))}
              className="px-1 py-1 border rounded text-xs">
              <option>EUR</option><option>USD</option><option>DT</option>
            </select>
          </div>
        </div>
      </div>
      <div>
        <label className="text-xs text-gray-500">Description</label>
        <input value={settings.description} onChange={e => setSettings(s => ({...s, description: e.target.value}))}
          className="w-full px-2 py-1 border rounded text-sm" placeholder="Brief description for affiliates..." />
      </div>
      <div className="flex gap-2">
        <button onClick={() => onSave(settings)} className="px-3 py-1 bg-emerald-600 text-white text-xs rounded hover:bg-emerald-700">Save</button>
        <button onClick={onCancel} className="px-3 py-1 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300">Cancel</button>
      </div>
    </div>
  );
};

export default Activities;
